import { useCallback, useEffect, useMemo, useState } from "react";

type UseDocumentFilesOptions = {
  multiple?: boolean;
  maxFiles?: number;
  onFilesAdded?: (files: File[]) => void;
  onFilesCleared?: () => void;
};

export function useDocumentFiles({
  multiple = false,
  maxFiles = multiple ? Infinity : 1,
  onFilesAdded,
  onFilesCleared,
}: UseDocumentFilesOptions = {}) {
  const [files, setFiles] = useState<File[]>([]);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  const handleFilesAdded = useCallback(
    (incoming: File[]) => {
      if (!incoming?.length) {
        return;
      }

      const filesToAdd = multiple ? incoming : [incoming[0]];

      setFiles((previousFiles) => {
        if (multiple) {
          const availableSlots = Math.max(0, maxFiles - previousFiles.length);
          if (availableSlots === 0) {
            return previousFiles;
          }
          const addedFiles = filesToAdd.slice(0, availableSlots);
          const nextFiles = [...previousFiles, ...addedFiles];
          if (addedFiles.length > 0) {
            onFilesAdded?.(addedFiles);
          }
          return nextFiles;
        }

        onFilesAdded?.(filesToAdd);
        return filesToAdd;
      });
    },
    [maxFiles, multiple, onFilesAdded]
  );

  const handleDrop = useCallback(
    (incoming: File[]) => {
      handleFilesAdded(incoming);
    },
    [handleFilesAdded]
  );

  const removeFile = useCallback(
    (index: number) => {
      setFiles((previousFiles) => {
        if (index < 0 || index >= previousFiles.length) {
          return previousFiles;
        }
        const next = [...previousFiles.slice(0, index), ...previousFiles.slice(index + 1)];
        if (next.length === 0) {
          onFilesCleared?.();
        }
        return next;
      });
    },
    [onFilesCleared]
  );

  const resetFiles = useCallback(() => {
    setFiles([]);
    setPreviewFile(null);
    onFilesCleared?.();
  }, [onFilesCleared]);

  const canAddMore = useMemo(() => {
    if (!multiple) {
      return files.length === 0;
    }
    return files.length < maxFiles;
  }, [files.length, maxFiles, multiple]);

  const openPreview = useCallback((file: File) => {
    setPreviewFile(file);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  const previewUrl = useMemo(() => {
    if (!previewFile) {
      return "";
    }
    return URL.createObjectURL(previewFile);
  }, [previewFile]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const hasPossibleDuplicates = useMemo(() => {
    // This should check name + size to see if there are any duplicates
    const nameAndSizeSet = new Set<string>();
    for (const file of files) {
      nameAndSizeSet.add(`${file.name}-${file.size}`);
    }
    return nameAndSizeSet.size < files.length;
  }, [files]);

  return {
    files,
    canAddMore,
    handleDrop,
    removeFile,
    resetFiles,
    previewFile,
    openPreview,
    closePreview,
    previewUrl,
    setFiles,
    hasPossibleDuplicates,
  };
}
